import {
  createCollection,
  localOnlyCollectionOptions,
} from '@tanstack/db'


import type { Addendum, AppSettings, Branding, Callout, Customer, CustomerDocument, CustomerNote, CustomerRevision, Document, Job, JobAssignment, Kiosk, KioskSession, Note, ServiceAssignment, ServiceRevision, StatusChangeLog, WhatsAppMessage, WhatsAppTemplateConfig, WhatsAppTwilioTemplate } from '@/lib/schemas/db'
import { getCurrentAssignment, getCurrentJobAssignment } from '@/lib/jobAssignments'
import { buildServiceRevisionChanges } from '@/lib/serviceRevisionFields'

import type {
  UserLookupPort,
  SaveBrandingInput,
  SaveWhatsAppTwilioSettingsInput,
  WhatsAppTwilioSettings,
} from '../ports'
import type { User } from '../domain/models'
import {
  isArchivedStatus,
  searchArchivedServiceRows,
  type ArchivedServiceRow,
  type ArchivedServicesSearchInput,
} from '../domain/archivedServices'
import {
  searchCustomerRows,
  type CustomersSearchInput,
} from '../domain/customerSearch'
import type {
  FollowUp,
  FollowUpCategory,
  FollowUpsSearchInput,
} from '../domain/followUps'
import { searchFollowUpRows } from '../domain/followUps'
import { randomIdGenerator, systemClock } from '../system'

export function createInMemoryTanStackDb(
  seedUsers: User[],
  opts?: { authStore?: { readonly user: User | null } },
) {
  /**
   * Mirror of the service-revisions PocketBase hook: in this adapter there is
   * no server, so the "backend" trail is written here — an onUpdate handler on
   * each service collection diffs the mutation and appends a revision. Demo
   * mode and tests therefore see the same add-only behaviour as production.
   */
  const recordServiceRevision = (
    target: () => { insert: (revision: ServiceRevision) => unknown },
    mutations: Array<{ original: unknown; modified: unknown; key: unknown }>,
  ) => {
    for (const mutation of mutations) {
      const changes = buildServiceRevisionChanges(
        (mutation.original ?? {}) as Record<string, unknown>,
        (mutation.modified ?? {}) as Record<string, unknown>,
      )
      if (changes.length === 0) continue
      target().insert({
        id: randomIdGenerator.generateId(),
        serviceId: String(mutation.key),
        authorId: opts?.authStore?.user?.id ?? '',
        createdAt: systemClock.now(),
        changes,
      })
    }
  }

  const usersCollection = createCollection(
      localOnlyCollectionOptions<User, User['id']>({
	  id: 'users',
	  getKey: (user: User) => user.id,
	  initialData: seedUsers || [],
      }),
  )

  const followUpCategoriesCollection = createCollection(
    localOnlyCollectionOptions<FollowUpCategory, FollowUpCategory['id']>({
      id: 'follow-up-categories',
      getKey: (category: FollowUpCategory) => category.id,
      // Mirrors the migration's seed so demo/storybook pickers have options.
      initialData: [
        { id: 'fucatlaptop0001', name: 'Laptop Purchase', sortOrder: 1 },
        { id: 'fucatlicense001', name: 'Office License Renewal', sortOrder: 2 },
        { id: 'fucatquotes0001', name: 'Quotes to do', sortOrder: 3 },
        { id: 'fucatprice00001', name: 'Price check', sortOrder: 4 },
        { id: 'fucatgeneral001', name: 'General', sortOrder: 5 },
        { id: 'fucatstaffreq01', name: 'Staff Requests', sortOrder: 6 },
      ],
    }),
  )

  const customersCollection = createCollection(
    localOnlyCollectionOptions<Customer, Customer['id']>({
      id: 'customers',
      getKey: (customer: Customer) => customer.id,
    }),
  )

  const archivedServicesCollection = createCollection(
    localOnlyCollectionOptions<ArchivedServiceRow, ArchivedServiceRow['key']>({
      id: 'archived-service-rows',
      getKey: (row: ArchivedServiceRow) => row.key,
    }),
  )

  const calloutsCollection = createCollection(
    localOnlyCollectionOptions<Callout, Callout['id']>({
      id: 'callouts',
      getKey: (callout: Callout) => callout.id,
      onUpdate: async ({ transaction }) => {
        recordServiceRevision(
          () => calloutRevisionsCollection,
          transaction.mutations,
        )
      },
    }),
  )

  const sessionsCollection = createCollection(
    localOnlyCollectionOptions<Callout, Callout['id']>({
      id: 'sessions',
      getKey: (session: Callout) => session.id,
      onUpdate: async ({ transaction }) => {
        recordServiceRevision(
          () => sessionRevisionsCollection,
          transaction.mutations,
        )
      },
    }),
  )

  const notesCollection = createCollection(
    localOnlyCollectionOptions<Note, Note['id']>({
      id: 'callout_notes',
      getKey: (note: Note) => note.id,
    }),
  )

  const sessionNotesCollection = createCollection(
    localOnlyCollectionOptions<Note, Note['id']>({
      id: 'session_notes',
      getKey: (note: Note) => note.id,
    }),
  )

  const jobNotesCollection = createCollection(
    localOnlyCollectionOptions<Note, Note['id']>({
      id: 'job_notes',
      getKey: (note: Note) => note.id,
    }),
  )

  const customerNotesCollection = createCollection(
    localOnlyCollectionOptions<CustomerNote, CustomerNote['id']>({
      id: 'customer_notes',
      getKey: (note: CustomerNote) => note.id,
    }),
  )

  const customerDocumentsCollection = createCollection(
    localOnlyCollectionOptions<CustomerDocument, CustomerDocument['id']>({
      id: 'customer_documents',
      getKey: (document: CustomerDocument) => document.id,
    }),
  )

  const followUpsCollection = createCollection(
    localOnlyCollectionOptions<FollowUp, FollowUp['id']>({
      id: 'follow_ups',
      getKey: (followUp: FollowUp) => followUp.id,
    }),
  )

  const statusChangesCollection = createCollection(
    localOnlyCollectionOptions<StatusChangeLog, StatusChangeLog['id']>({
      id: 'callout_status_changes',
      getKey: (statusChange: StatusChangeLog) => statusChange.id,
    }),
  )

  const sessionStatusChangesCollection = createCollection(
    localOnlyCollectionOptions<StatusChangeLog, StatusChangeLog['id']>({
      id: 'session_status_changes',
      getKey: (statusChange: StatusChangeLog) => statusChange.id,
    }),
  )

  const jobStatusChangesCollection = createCollection(
    localOnlyCollectionOptions<StatusChangeLog, StatusChangeLog['id']>({
      id: 'job_status_changes',
      getKey: (statusChange: StatusChangeLog) => statusChange.id,
    }),
  )

  const addendumsCollection = createCollection(
    localOnlyCollectionOptions<Addendum, Addendum['id']>({
      id: 'callout_addendums',
      getKey: (addendum: Addendum) => addendum.id,
    }),
  )

  const sessionAddendumsCollection = createCollection(
    localOnlyCollectionOptions<Addendum, Addendum['id']>({
      id: 'session_addendums',
      getKey: (addendum: Addendum) => addendum.id,
    }),
  )

  const jobAddendumsCollection = createCollection(
    localOnlyCollectionOptions<Addendum, Addendum['id']>({
      id: 'job_addendums',
      getKey: (addendum: Addendum) => addendum.id,
    }),
  )

  const customerRevisionsCollection = createCollection(
    localOnlyCollectionOptions<CustomerRevision, CustomerRevision['id']>({
      id: 'customer_revisions',
      getKey: (revision: CustomerRevision) => revision.id,
    }),
  )

  const calloutRevisionsCollection = createCollection(
    localOnlyCollectionOptions<ServiceRevision, ServiceRevision['id']>({
      id: 'callout_revisions',
      getKey: (revision: ServiceRevision) => revision.id,
    }),
  )

  const sessionRevisionsCollection = createCollection(
    localOnlyCollectionOptions<ServiceRevision, ServiceRevision['id']>({
      id: 'session_revisions',
      getKey: (revision: ServiceRevision) => revision.id,
    }),
  )

  const jobRevisionsCollection = createCollection(
    localOnlyCollectionOptions<ServiceRevision, ServiceRevision['id']>({
      id: 'job_revisions',
      getKey: (revision: ServiceRevision) => revision.id,
    }),
  )

  const jobAssignmentsCollection = createCollection(
    localOnlyCollectionOptions<JobAssignment, JobAssignment['id']>({
      id: 'job_assignments',
      getKey: (assignment: JobAssignment) => assignment.id,
    }),
  )

  const calloutAssignmentsCollection = createCollection(
    localOnlyCollectionOptions<ServiceAssignment, ServiceAssignment['id']>({
      id: 'callout_assignments',
      getKey: (assignment: ServiceAssignment) => assignment.id,
    }),
  )

  const sessionAssignmentsCollection = createCollection(
    localOnlyCollectionOptions<ServiceAssignment, ServiceAssignment['id']>({
      id: 'session_assignments',
      getKey: (assignment: ServiceAssignment) => assignment.id,
    }),
  )

  const jobsCollection = createCollection(
    localOnlyCollectionOptions<Job, Job['id']>({
      id: 'jobs',
      getKey: (job: Job) => job.id,
      onUpdate: async ({ transaction }) => {
        recordServiceRevision(
          () => jobRevisionsCollection,
          transaction.mutations,
        )
      },
    }),
  )

  const kiosksCollection = createCollection(
    localOnlyCollectionOptions<Kiosk, Kiosk['id']>({
      id: 'kiosks',
      getKey: (kiosk: Kiosk) => kiosk.id,
    }),
  )

  const kioskSessionsCollection = createCollection(
    localOnlyCollectionOptions<KioskSession, KioskSession['id']>({
      id: 'kiosk_sessions',
      getKey: (session: KioskSession) => session.id,
    }),
  )

  const documentsCollection = createCollection(
    localOnlyCollectionOptions<Document, Document['id']>({
      id: 'documents',
      getKey: (document: Document) => document.id,
    }),
  )

  const whatsappMessagesCollection = createCollection(
    localOnlyCollectionOptions<WhatsAppMessage, WhatsAppMessage['id']>({
      id: 'whatsapp_messages',
      getKey: (message: WhatsAppMessage) => message.id,
    }),
  )

  const appSettingsCollection = createCollection(
    localOnlyCollectionOptions<AppSettings, AppSettings['id']>({
      id: 'app_settings',
      getKey: (settings: AppSettings) => settings.id,
      initialData: [
        {
          id: 'appsettings0001',
          createdAt: '2026-01-01T00:00:00.000Z',
          updatedAt: '2026-01-01T00:00:00.000Z',
          whatsappSendDelaySeconds: 30,
          whatsappSendingEnabled: false,
        },
      ],
    }),
  )

  const brandingCollection = createCollection(
    localOnlyCollectionOptions<Branding, Branding['id']>({
      id: 'branding',
      getKey: (branding: Branding) => branding.id,
      initialData: [
        {
          id: 'branding0000001',
          createdAt: '2026-01-01T00:00:00.000Z',
          updatedAt: '2026-01-01T00:00:00.000Z',
          name: 'Acme Computers',
        },
      ],
    }),
  )

  // Uploaded logos become object URLs so the demo/preview shows them; there
  // is no server to store the bytes.
  const localFileUrl = (file: File) =>
    typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
      ? URL.createObjectURL(file)
      : file.name

  const saveBranding = async (input: SaveBrandingInput) => {
    const current = Array.from(brandingCollection.values())[0]
    if (!current) throw new Error('Branding record not found')
    const transaction = brandingCollection.update(current.id, (draft) => {
      draft.name = input.name.trim()
      draft.updatedAt = new Date().toISOString()
      if (input.logoFile) draft.logoUrl = localFileUrl(input.logoFile)
      else if (input.removeLogo) draft.logoUrl = undefined
      if (input.logoOnLightFile) draft.logoOnLightUrl = localFileUrl(input.logoOnLightFile)
      else if (input.removeLogoOnLight) draft.logoOnLightUrl = undefined
    })
    await transaction.isPersisted.promise
  }

  const whatsappTemplatesCollection = createCollection(
    localOnlyCollectionOptions<
      WhatsAppTemplateConfig,
      WhatsAppTemplateConfig['id']
    >({
      id: 'whatsapp_templates',
      getKey: (template: WhatsAppTemplateConfig) => template.id,
      initialData: [
        {
          id: 'watplrfcsuccess',
          createdAt: '2026-01-01T00:00:00.000Z',
          updatedAt: '2026-01-01T00:00:00.000Z',
          key: 'ready_for_collection_successful',
          label: 'Ready for collection — successful',
          contentSid: undefined,
          variables: {},
        },
        {
          id: 'watplrfcunsucce',
          createdAt: '2026-01-01T00:00:00.000Z',
          updatedAt: '2026-01-01T00:00:00.000Z',
          key: 'ready_for_collection_unsuccessful',
          label: 'Ready for collection — unsuccessful',
          contentSid: undefined,
          variables: {},
        },
      ],
    }),
  )

  const whatsappTwilioTemplatesCollection = createCollection(
    localOnlyCollectionOptions<
      WhatsAppTwilioTemplate,
      WhatsAppTwilioTemplate['id']
    >({
      id: 'whatsapp_twilio_templates',
      getKey: (template: WhatsAppTwilioTemplate) => template.id,
    }),
  )

  async function requestTwilioTemplateSync(): Promise<{
    ok: boolean
    result?: string
  }> {
    // No Twilio in the in-memory adapter; the cache stays as seeded.
    return { ok: true, result: 'ok:0' }
  }

  // In-memory stand-in for the admin Twilio-settings routes. Mirrors the
  // server behaviour: the token is write-only, only hasAuthToken is reported.
  let twilioSettingsState = {
    accountSid: '',
    fromNumber: '',
    messagingServiceSid: '',
    authToken: '',
  }

  async function loadWhatsAppTwilioSettings(): Promise<WhatsAppTwilioSettings> {
    const { authToken, ...rest } = twilioSettingsState
    return { ...rest, hasAuthToken: authToken !== '' }
  }

  async function saveWhatsAppTwilioSettings(
    input: SaveWhatsAppTwilioSettingsInput,
  ): Promise<WhatsAppTwilioSettings> {
    const authToken = input.authToken?.trim()
      ? input.authToken.trim()
      : input.clearAuthToken
        ? ''
        : twilioSettingsState.authToken
    twilioSettingsState = {
      accountSid: input.accountSid.trim(),
      fromNumber: input.fromNumber.trim(),
      messagingServiceSid: input.messagingServiceSid.trim(),
      authToken,
    }
    return loadWhatsAppTwilioSettings()
  }

  async function getFreshJob(jobId: string): Promise<Job | undefined> {
    return jobsCollection.get?.(jobId) as Job | undefined
  }

  const usersLookup: UserLookupPort = {
    getById(id) {
      return (usersCollection).get?.(id)
        ?? seedUsers.find((user) => user.id === id)
    },
  }

  // Mirrors the PocketBase archived_services view over the local collections.
  async function searchArchivedServices(input: ArchivedServicesSearchInput) {
    const customersById = new Map(
      customersCollection.toArray.map((customer) => [customer.id, customer]),
    )
    const usersById = new Map<string, User>(
      usersCollection.toArray.map((user) => [user.id, user as User]),
    )
    const assignmentsByJobId = new Map<string, JobAssignment[]>()
    for (const assignment of jobAssignmentsCollection.toArray) {
      const current = assignmentsByJobId.get(assignment.jobId) ?? []
      current.push(assignment)
      assignmentsByJobId.set(assignment.jobId, current)
    }

    const serviceAssignmentsByServiceId = (assignments: ServiceAssignment[]) => {
      const map = new Map<string, ServiceAssignment[]>()
      for (const assignment of assignments) {
        const current = map.get(assignment.serviceId) ?? []
        current.push(assignment)
        map.set(assignment.serviceId, current)
      }
      return map
    }
    const calloutAssignments = serviceAssignmentsByServiceId(
      calloutAssignmentsCollection.toArray,
    )
    const sessionAssignments = serviceAssignmentsByServiceId(
      sessionAssignmentsCollection.toArray,
    )

    const bookingRow = (
      record: Callout,
      type: 'callout' | 'remote' | 'sitdown',
    ): ArchivedServiceRow => {
      const customer = customersById.get(record.customerId ?? '')
      const assignment = getCurrentAssignment(
        (type === 'callout' ? calloutAssignments : sessionAssignments).get(record.id) ?? [],
        usersById,
      )
      return {
        key: `${type === 'callout' ? 'callout' : 'session'}:${record.id}`,
        type,
        recordId: record.id,
        publicId: record.publicId ?? '',
        customerId: record.customerId,
        customerPublicId: customer?.publicId,
        customerName: record.contactName || customer?.fullName || '',
        summary: record.issueSummary ?? '',
        bookedAt: record.startTime,
        isWholeDay: Boolean(record.isWholeDay),
        assigneeName: assignment
          ? assignment.user?.name || assignment.user?.email || undefined
          : undefined,
        status: record.status,
        updatedAt: record.updatedAt ?? '',
      }
    }

    const rows: ArchivedServiceRow[] = [
      ...calloutsCollection.toArray
        .filter((callout) => isArchivedStatus(callout.status))
        .map((callout) => bookingRow(callout, 'callout')),
      ...sessionsCollection.toArray
        .filter((session) => isArchivedStatus(session.status))
        .map((session) =>
          bookingRow(
            session,
            session.sessionSubtype === 'Sit-down Session' ? 'sitdown' : 'remote',
          ),
        ),
      ...jobsCollection.toArray
        .filter((job) => isArchivedStatus(job.status))
        .map((job): ArchivedServiceRow => {
          const customer = customersById.get(job.customerId ?? '')
          const assignment = getCurrentJobAssignment(
            assignmentsByJobId.get(job.id) ?? [],
            usersById,
          )
          return {
            key: `job:${job.id}`,
            type: 'job',
            recordId: job.id,
            publicId: job.publicId ?? '',
            customerId: job.customerId,
            customerPublicId: customer?.publicId,
            customerName: customer?.fullName || job.contactName || 'Unknown customer',
            summary: job.fault,
            bookedAt: job.createdAt,
            isWholeDay: false,
            assigneeName: assignment
              ? assignment.user?.name || assignment.user?.email || undefined
              : undefined,
            status: job.status,
            updatedAt: job.updatedAt ?? '',
          }
        }),
    ]

    const { items, ...meta } = searchArchivedServiceRows(rows, input)
    for (const row of items) {
      if (archivedServicesCollection.has(row.key)) {
        archivedServicesCollection.update(row.key, (draft) => {
          Object.assign(draft, row)
        })
      } else {
        archivedServicesCollection.insert(row)
      }
    }
    return { ...meta, keys: items.map((row) => row.key) }
  }

  // Mirrors the PocketBase customers search over the local collection. The
  // records are already in the customers collection, so only keys are needed.
  async function searchCustomers(input: CustomersSearchInput) {
    const { items, ...meta } = searchCustomerRows(customersCollection.toArray, input)
    return { ...meta, keys: items.map((customer) => customer.id) }
  }

  // Mirrors the PocketBase follow-ups search over the local collections; only
  // keys are needed since everything already lives in the collection.
  async function searchFollowUps(input: FollowUpsSearchInput) {
    const customersById = new Map(
      customersCollection.toArray.map((customer) => [customer.id, customer]),
    )
    const { items, ...meta } = searchFollowUpRows(
      followUpsCollection.toArray,
      (customerId) => customersById.get(customerId)?.fullName ?? '',
      input,
    )
    return { ...meta, keys: items.map((followUp) => followUp.id) }
  }

  return {
    usersCollection,
    customersCollection,
    calloutsCollection,
    sessionsCollection,
    notesCollection,
    sessionNotesCollection,
    jobNotesCollection,
    customerNotesCollection,
    customerDocumentsCollection,
    followUpsCollection,
    followUpCategoriesCollection,
    statusChangesCollection,
    sessionStatusChangesCollection,
    jobStatusChangesCollection,
    addendumsCollection,
    sessionAddendumsCollection,
    jobAddendumsCollection,
    customerRevisionsCollection,
    calloutRevisionsCollection,
    sessionRevisionsCollection,
    jobRevisionsCollection,
    jobAssignmentsCollection,
    calloutAssignmentsCollection,
    sessionAssignmentsCollection,
    jobsCollection,
    kiosksCollection,
    kioskSessionsCollection,
    documentsCollection,
    whatsappMessagesCollection,
    appSettingsCollection,
    brandingCollection,
    saveBranding,
    whatsappTemplatesCollection,
    whatsappTwilioTemplatesCollection,
    requestTwilioTemplateSync,
    loadWhatsAppTwilioSettings,
    saveWhatsAppTwilioSettings,
    getFreshJob,
    usersLookup,
    archivedServicesCollection,
    searchArchivedServices,
    // In memory the collections *are* the store, so the "scoped fetch" is a
    // plain filter over them.
    // Every note is already in its collection here; screens read them back
    // through live queries, so there is nothing extra to load.
    loadServiceNotes: async () => {},
    searchCustomers,
    searchFollowUps,
    // Everything is already local, so there is nothing extra to load.
    loadCustomerFollowUps: async () => {},
    // No protected file storage in memory — an empty token opens local URLs.
    getFileToken: async () => '',
    // Everything is already local in this adapter, so a record that isn't in
    // its collection simply doesn't exist.
    hydrateServiceDetails: async () => false,
    hydrateRecord: async () => true,
    updateCustomerDirect: async (id: string, patch: Partial<Customer>) => {
      if (!customersCollection.get(id)) {
        throw new Error(`Customer ${id} not found`)
      }
      const transaction = customersCollection.update(id, (draft) => {
        Object.assign(draft, patch)
      })
      await transaction.isPersisted.promise
      const updated = customersCollection.get(id)
      if (!updated) {
        throw new Error(`Customer ${id} not found`)
      }
      return updated
    },
  }
}

export type InMemoryTanStackDb = ReturnType<typeof createInMemoryTanStackDb>
